<template>
  <div class="my-case-icon-wrapper">
    <Tippy
      ref="tippy"
      :arrow="true"
      theme="light"
      trigger="manual"
      :hide-on-click="false"
      content="Added to case!"
      animation="shift-away"
      append-to="parent"
      placement="bottom"
      :popper-options="{ strategy: 'fixed' }">
      <div
        class="my-case-icon"
        :class="{'mobile': isMobile}">
        <SvgCase height="22"/>
        <SvgPause
          v-if="iconState === 'pause'"
          class="pause state-indicator"/>
        <MyCaseIconCounter
          v-else-if="iconState === 'plus' || iconState === numSelectedItems"
          class="icon-counter"/>
        <MyCaseIconInTransit
          v-else-if="iconState === 'transit'"
          class="transit state-indicator"/>
        <SvgRefreshCW
          v-else-if="iconState === 'swap-available'"
          class="state-indicator swap"/>
        <MyCaseIconAtHome
          v-else-if="iconState === 'with-me'"
          class="home state-indicator"/>
      </div>
    </Tippy>
  </div>
</template>

<script setup>
import MyCaseIconInTransit from './MyCaseIconInTransit'
import MyCaseIconAtHome from './MyCaseIconAtHome'
import MyCaseIconCounter from './MyCaseIconCounter'
import SvgCase from '@/components/global/svg/SvgCase.vue'
import SvgPause from '@/components/global/svg/SvgPause.vue'
import SvgRefreshCW from '@/components/global/svg/SvgRefreshCW.vue'
import { Tippy } from 'vue-tippy'
import { useClosetStore } from '@shared/stores/closet.js'
import { storeToRefs } from 'pinia'
import { useClientStore } from '@shared/stores/client.js'
import { useCaseStore } from '@/stores/case.js'
import { computed, useTemplateRef, watch } from 'vue'

const { selectedItemsLoaded, packageItemsLoaded, deliveredItems } = storeToRefs(useClosetStore())
const { addedToCase } = storeToRefs(useCaseStore())
const { membershipStatus, tooManyItemsOut, canSelectItems, numSelectedItems, itemsInTransit, membershipDetails } = storeToRefs(useClientStore())

const iconState = computed(() => {
  if (!selectedItemsLoaded.value || !packageItemsLoaded.value) return 'empty'

  if (membershipStatus.value !== 'active' || tooManyItemsOut.value) {
    return 'pause'
  }

  if (canSelectItems.value) {
    if (numSelectedItems.value) {
      return numSelectedItems.value
    } else {
      return 'plus'
    }
  }

  if (itemsInTransit.value) {
    return 'transit'
  }

  if (membershipDetails.value.allowedShipments > 0) {
    return 'swap-available'
  }

  if (deliveredItems.value.length) {
    return 'with-me'
  }

  return 'empty'
})

const tippy = useTemplateRef('tippy')
watch(addedToCase, (val) => {
  if (val) {
    tippy.value.show()
  } else {
    tippy.value.hide()
  }
})
</script>

<style lang="scss" scoped>
.my-case-icon-wrapper {
  &:focus {
    border: none;
    outline: none;
  }
}

.my-case-icon {
  position: relative;
}

:deep(.tippy-content) {
  color: $pewter;
  font-weight: $font-weight-normal;
}

.icon-counter {
  position: absolute;
  bottom: -4px;
  right: -3px;
}

.state-indicator {
  position: absolute;
  left: 2px;
  right: 2px;
  top: 9px;
  width: 21px;

  &.transit {
    top: 11px;
    width: 20px;
    margin-left: -2px;
  }

  &.pause {
    top: 11px;
  }

  &.swap {
    top: 9px;
    height: 12px;
    width: 12px;
    left: 7px;
    stroke-width: 3px;
  }
}
</style>
